// https://github.com/nonsalant/simple-tabs

/**
 * @typedef {Object} SimpleTabs
 */

/**
 * A custom element that renders tabs and switches to accordion on smaller screens
 *
 */

class SimpleTabs extends HTMLElement {
  constructor() {
    super();
    this.selector = '[role=tabs]';
    this.maxElementsToAnimate = 20;
  }

  connectedCallback() {
    setTimeout(() => {
      this.#enableViewTransitions();
    });
  }

  // use document.querySelector('simple-tabs').sections
  get sections() {
    return this.querySelectorAll('label');
  }

  // use document.querySelector('simple-tabs').activeSection = 'AZ0ZqN2hXcmFEdEtiY__tab_RHy9r7';
  set activeSection(sectionId) {
    return this.querySelector(`#${sectionId} input`).click();
  }

  #enableViewTransitions() {
    const accumulateNextElements = (limit = 0) => {
      const internalSelector = `${this.selector}, ${this.selector} > *`;
      const internalCollection = this.querySelectorAll(internalSelector);
      const internalElements = Array.from(internalCollection);

      let currentEl = this;
      let resultArr = [document.querySelector('body')];
      // Recursively accumulate elements that follow in the DOM and are not hidden
      while (currentEl.parentNode && currentEl.parentNode.tagName !== 'HTML') {
        const parent = currentEl.parentNode;
        let siblings = Array.from(parent.children).filter(
          (el) => !isHidden(el),
        );
        siblings = siblings.slice(siblings.indexOf(currentEl) + 1);
        resultArr = [...resultArr, ...siblings];

        // Apply the limit if it's reached
        if (limit > 0 && limit < resultArr.length) {
          resultArr = resultArr.slice(0, limit);
          break;
        }

        currentEl = currentEl.parentNode;
      }

      return [...internalElements, ...resultArr];
    };

    // Enable view transitions if supported
    if (document.startViewTransition) {
      const triggers = this.querySelectorAll('[role=tab]:has([type=radio])');
      triggers.forEach((item) => {
        let target = item.querySelector('[type=radio]');

        item.addEventListener('click', (e) => {
          // If the click already came from the radio input itself,
          // we don't want to prevent default or manually toggle.
          if (e.target === target) return;

          e.preventDefault(); // Prevent default label behavior to control it via View Transition

          const transition = document.startViewTransition(() => {
            target.checked = !target.checked;
          });

          const toTransition = accumulateNextElements(
            this.maxElementsToAnimate,
          );
          addTransitionNames(toTransition); // add temp vt names
          transition.ready.then(() => {
            removeTransitionNames(toTransition); // clean up vt names
          });
        });
      });
    }

    // View Transition utils
    function addTransitionNames(arr) {
      arr.forEach((el, i) => {
        if (el.style.viewTransitionName === '') {
          el.style.viewTransitionName = 'simple-tabs-transition-' + i;
        }
      });
    }
    function removeTransitionNames(arr) {
      arr.forEach((el, i) => {
        if (el.style.viewTransitionName.startsWith('simple-tabs-transition-')) {
          el.style.viewTransitionName = '';
        }
      });
    }
    // Helper function to check if an element has display: none or equivalent
    function isHidden(el) {
      const style = window.getComputedStyle(el);
      return style.display === 'none' || style.visibility === 'hidden';
    }
  }
}

customElements.define('simple-tabs', SimpleTabs);
